import type { ComponentPropsWithoutRef, ReactNode } from "react";

const topBarClassName = [
  "flex",
  "h-(--spacing-64)",
  "w-full",
  "items-center",
  "bg-surface",
  "border-b",
  "border-outline-subtle",
  "max-[599px]:h-[68px]",
];

const topBarLeadingClassName = [
  "flex",
  "h-full",
  "min-w-0",
  "shrink-0",
  "transition-[width]",
  "duration-200",
  "ease-in-out",
  "items-center",
  "border-r",
  "border-outline-subtle",
  "px-(--spacing-16)",
  "max-[599px]:border-r-0",
  "max-[599px]:px-(--spacing-24)",
];

const topBarContentClassName = [
  "hidden",
  "h-full",
  "min-w-0",
  "flex-1",
  "items-center",
  "overflow-hidden",
  "px-(--spacing-24)",
  "min-[600px]:flex",
];

const topBarActionsClassName = [
  "ml-auto",
  "flex",
  "h-full",
  "shrink-0",
  "items-center",
  "gap-(--spacing-12)",
  "pr-(--spacing-24)",
  "max-[599px]:gap-(--spacing-20)",
];

export interface TopBarProps
  extends Omit<ComponentPropsWithoutRef<"header">, "children" | "className"> {
  children: ReactNode;
}

/**
 * Responsive application header layout.
 *
 * @example
 * <TopBar>
 *   <TopBarLeading><AssetWatchLogo /></TopBarLeading>
 *   <TopBarContent><Breadcrumb>...</Breadcrumb></TopBarContent>
 *   <TopBarActions><IconButton icon="search" aria-label="Search" /></TopBarActions>
 * </TopBar>
 */
function TopBar({ children, ...props }: TopBarProps) {
  return (
    <header
      {...props}
      className={topBarClassName.join(" ")}
      data-slot="top-bar"
    >
      {children}
    </header>
  );
}

export interface TopBarLeadingProps
  extends Omit<ComponentPropsWithoutRef<"div">, "children" | "className"> {
  children: ReactNode;
}

/**
 * Leading header region for consumer-provided branding or shell controls.
 * The application shell controls this region's desktop width.
 */
function TopBarLeading({ children, ...props }: TopBarLeadingProps) {
  return (
    <div
      {...props}
      className={topBarLeadingClassName.join(" ")}
      data-slot="top-bar-leading"
    >
      {children}
    </div>
  );
}

export interface TopBarContentProps
  extends Omit<ComponentPropsWithoutRef<"div">, "children" | "className"> {
  children: ReactNode;
}

/**
 * Main header content region. Hidden at and below the mobile breakpoint.
 */
function TopBarContent({ children, ...props }: TopBarContentProps) {
  return (
    <div
      {...props}
      className={topBarContentClassName.join(" ")}
      data-slot="top-bar-content"
    >
      {children}
    </div>
  );
}

export interface TopBarActionsProps
  extends Omit<ComponentPropsWithoutRef<"div">, "children" | "className"> {
  children: ReactNode;
}

/**
 * Trailing header region for consumer-provided actions.
 */
function TopBarActions({ children, ...props }: TopBarActionsProps) {
  return (
    <div
      {...props}
      className={topBarActionsClassName.join(" ")}
      data-slot="top-bar-actions"
    >
      {children}
    </div>
  );
}

export { TopBar, TopBarActions, TopBarContent, TopBarLeading };
